import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PriorityPill, StatusPill } from "@/components/Pills";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, UserPlus } from "lucide-react";

export default function MyCustomers() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [priority, setPriority] = useState("All");

  const load = useCallback(() => {
    api.get("/customers", { params: { search, category, priority } }).then((r) => setRows(r.data));
  }, [search, category, priority]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const chips = ["All", ...meta.categories];

  return (
    <div>
      <PageHeader
        title="My Customers"
        subtitle={`${rows.length} leads in your book`}
        actions={<Button data-testid="add-customer-btn" onClick={() => navigate("/app/add-customer")} className="bg-[#0F4C81] hover:bg-[#0A3A66]"><UserPlus size={16} className="mr-2" />Add Customer</Button>}
      />
      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search business, contact or mobile…" className="pl-9" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {chips.map((c) => (
              <button key={c} data-testid={`cat-chip-${c}`} onClick={() => setCategory(c)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border ${category === c ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0F4C81]"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {["All", ...meta.priorities].map((p) => (
              <button key={p} data-testid={`prio-chip-${p}`} onClick={() => setPriority(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${priority === p ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200"}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Business</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Loan</TableHead>
              <TableHead>Mela</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/app/customers/${c.id}`)}>
                <TableCell className="font-semibold text-slate-900">{c.business_name}</TableCell>
                <TableCell><div className="text-sm">{c.contact_person}</div><div className="text-xs text-slate-400">{c.mobile}</div></TableCell>
                <TableCell><span className="text-sm text-slate-600">{c.category}</span></TableCell>
                <TableCell><PriorityPill value={c.priority} /></TableCell>
                <TableCell className="font-mono text-sm">{inr(c.loan_amount)}</TableCell>
                <TableCell><span className={`text-sm font-semibold ${c.mela_interest === "Yes" ? "text-emerald-600" : "text-slate-400"}`}>{c.mela_interest || "—"}</span></TableCell>
                <TableCell><StatusPill value={c.lead_status} /></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No customers found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
